/**
 * Chat Header Component
 *
 * Compact header for the chat sidebar with:
 * - Left: Close button to hide the chat sidebar
 * - Right: New chat button to start a fresh conversation
 *
 * Note: When used inside SidebarContainer, the header is hidden (hideHeader=true)
 * and SidebarContainer provides its own unified header with toggle support.
 */

import React from 'react';
import { EditIcon } from '@jotpsych/ui/icons/EditIcon';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface ChatHeaderProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Callback when close button is clicked
   */
  onClose: () => void;

  /**
   * Callback when clear chat button is clicked
   */
  onClearChat: () => void;

  /**
   * Whether there are messages to clear
   * Used to disable the button when chat is empty
   */
  hasMessages: boolean;
}

/**
 * ChatHeader - Compact header for chat sidebar
 *
 * Provides:
 * - Close button (left) to hide the chat sidebar
 * - Clear button (right) to reset the chat thread
 */
export const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onClearChat,
  hasMessages,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 48,
        px: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Close Chat Button */}
      <Tooltip title="Hide chat" placement="right">
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
              bgcolor: theme.palette.action.hover,
            },
            transition: 'all 0.2s ease',
          }}
          aria-label="Hide chat"
        >
          <ChevronRightIcon />
        </IconButton>
      </Tooltip>

      {/* New Chat Button */}
      <Tooltip title="Start new chat" placement="left">
        <span>
          <IconButton
            onClick={onClearChat}
            disabled={!hasMessages}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: theme.palette.primary.light + '20',
              },
              '&:disabled': {
                color: theme.palette.action.disabled,
              },
              transition: 'all 0.2s ease',
            }}
            aria-label="Start new chat"
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
